/**
 * Typed React Query hooks for every endpoint in docs/SPEC.md §4.
 *
 * Queries take ids that may be undefined (straight from useParams) and stay idle until the id
 * exists. Mutations update the cache with what the server returns and invalidate whatever
 * the change can affect; live updates from other devices arrive through useGalleyEvents.
 */
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import type {
  ComfortSettings,
  CourseDTO,
  CourseworkDTO,
  DebriefDTO,
  DisplayType,
  FamilyLinkDTO,
  FamilyViewDTO,
  GalleyDetailDTO,
  GalleySettingsDTO,
  GalleySummaryDTO,
  GradeBand,
  Highlight,
  JoinLookupDTO,
  MemberDTO,
  ModerationAction,
  NoteDTO,
  NoteInput,
  NoteValidation,
  PieceDetailDTO,
  PieceStatus,
  PieceSummaryDTO,
  PieceTeacherDTO,
  PieceVisibility,
  ReflectionInput,
  ResolveLinkDTO,
  ReturnDTO,
  ReviewQueueDTO,
  RoomDetailDTO,
  RoomStatus,
  SessionDTO,
  SignInCardDTO,
  StopDTO,
  StudentDTO,
  StudentPieceDTO,
  StudentWalkDTO,
  TechKitDTO,
  ThemeId,
  WalkConsoleDTO,
  WalkSettingsInput,
  WorldDTO,
} from '@galley/shared'
import { api, apiUrl, setCsrfToken, type ApiError } from './client'
import { keys } from './keys'

// ---------------------------------------------------------------------------
// Response shapes that exist only in docs/SPEC.md §4 (not named in @galley/shared)
// ---------------------------------------------------------------------------

/** GET /api/s/me */
export interface StudentMeDTO {
  student: StudentDTO
  galley: { id: string; name: string; theme: ThemeId; gradeBand: GradeBand }
  walk: StudentWalkDTO | null
  myPieces: StudentPieceDTO[]
}

/** GET /api/t/galleys/:id/cards */
export interface SignInCardsDTO {
  doorCode: string
  joinUrl: string
  cards: SignInCardDTO[]
}

/** GET /api/join/choices */
export interface JoinChoicesDTO {
  galleys: Array<{ id: string; name: string }>
}

/** POST /api/s/notes */
export interface SendNoteResult {
  note: NoteDTO
  validation: NoteValidation
  stop: StopDTO
}

export type WalkControlAction =
  'pause' | 'resume' | 'lock' | 'unlock' | 'end' | 'reopen' | 'delete' | 'restore'

export interface GalleyPatch {
  name?: string
  gradeBand?: GradeBand
  theme?: ThemeId
  settings?: Partial<GalleySettingsDTO>
  reseedDecor?: true
  /** Undo a delete within 30 days. */
  restore?: true
}

export interface RoomPatch {
  title?: string
  signText?: string
  displayOverride?: DisplayType | null
  studentCheck?: boolean
  status?: RoomStatus
}

/** PATCH /api/t/pieces/:id (SPEC §4.2). */
export interface PiecePatch {
  display?: DisplayType
  visibility?: PieceVisibility
  status?: Extract<PieceStatus, 'active' | 'hidden'>
  /** 1–160 characters after trimming. */
  title?: string
  /** Up to 500 characters; null (or an empty string) clears it. */
  imageAlt?: string | null
}

export interface MemberPatch {
  neverShowName?: boolean
  neverFamily?: boolean
  dontExhibit?: boolean
  excuseVisiting?: boolean
  glowWonderOnly?: boolean
}

export interface MyPiecePatch {
  visibility?: PieceVisibility
  noticeThis?: string | null
  highlight?: Highlight | null
  visitorQuestion?: string | null
  blurb?: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const enc = encodeURIComponent

/** Store a SessionDTO returned by any sign-in endpoint and adopt its CSRF token. */
function adoptSession(qc: QueryClient, session: SessionDTO): void {
  setCsrfToken(session.csrf)
  qc.setQueryData(keys.session(), session)
}

/** A different person may now be signed in: drop everything cached for the old one. */
function forgetViewerData(qc: QueryClient, role: 'teacher' | 'student'): void {
  qc.removeQueries({ queryKey: role === 'teacher' ? keys.teacher.all() : keys.student.all() })
  qc.removeQueries({ queryKey: keys.world.all() })
}

function patchStop(qc: QueryClient, stop: StopDTO): void {
  qc.setQueryData<StudentMeDTO>(keys.student.me(), (me) =>
    me?.walk
      ? {
          ...me,
          walk: { ...me.walk, stops: me.walk.stops.map((s) => (s.id === stop.id ? stop : s)) },
        }
      : me,
  )
}

// ---------------------------------------------------------------------------
// §4.1 Session & auth
// ---------------------------------------------------------------------------

export function fetchSession(): Promise<SessionDTO> {
  return api<SessionDTO>('/api/session')
}

export function useSessionQuery(options: { enabled?: boolean } = {}) {
  return useQuery<SessionDTO, ApiError>({
    queryKey: keys.session(),
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: 1,
    enabled: options.enabled ?? true,
  })
}

/** Full-page navigation target that starts Google sign-in (302 to Google). */
export function googleStartUrl(options: {
  role: 'teacher' | 'student'
  next?: string
  scope?: 'drive'
}): string {
  return apiUrl('/api/auth/google/start', options)
}

export function useDemoSignIn() {
  const qc = useQueryClient()
  return useMutation<SessionDTO, ApiError, void>({
    mutationFn: () => api<SessionDTO>('/api/auth/demo', { method: 'POST' }),
    onSuccess: (session) => {
      forgetViewerData(qc, 'teacher')
      adoptSession(qc, session)
    },
  })
}

/** Demo sandboxes only: open a student session in this browser ("See it as a student"). */
export function useDemoStudent() {
  const qc = useQueryClient()
  return useMutation<SessionDTO, ApiError, { memberId: string }>({
    mutationFn: (body) => api<SessionDTO>('/api/auth/demo/student', { method: 'POST', body }),
    onSuccess: (session) => {
      forgetViewerData(qc, 'student')
      adoptSession(qc, session)
    },
  })
}

/** Other tabs of this browser hear about a sign-out and re-check the session (SessionProvider). */
export const SESSION_CHANNEL = 'galley.session'

function announceSignOut(): void {
  if (typeof BroadcastChannel === 'undefined') return
  const channel = new BroadcastChannel(SESSION_CHANNEL)
  channel.postMessage({ type: 'signed-out' })
  channel.close()
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { role: 'teacher' | 'student' }>({
    mutationFn: (body) => api<void>('/api/auth/logout', { method: 'POST', body }),
    onSuccess: (_data, { role }) => {
      forgetViewerData(qc, role)
      announceSignOut()
      return qc.invalidateQueries({ queryKey: keys.session() })
    },
  })
}

export function useJoinLookup() {
  return useMutation<JoinLookupDTO, ApiError, { doorCode: string }>({
    mutationFn: (body) => api<JoinLookupDTO>('/api/join/lookup', { method: 'POST', body }),
  })
}

/**
 * Sign in with a printed card. Wrong pictures answer 401 with `details.remaining` tries left;
 * too many tries answer 423 (locked for a while).
 */
export function useJoinCard() {
  const qc = useQueryClient()
  return useMutation<
    SessionDTO,
    ApiError,
    { doorCode: string; memberId: string; pictures: [string, string, string] }
  >({
    mutationFn: (body) => api<SessionDTO>('/api/join/card', { method: 'POST', body }),
    onSuccess: (session) => {
      forgetViewerData(qc, 'student')
      adoptSession(qc, session)
    },
  })
}

export function useJoinQr() {
  const qc = useQueryClient()
  return useMutation<SessionDTO, ApiError, { token: string }>({
    mutationFn: (body) => api<SessionDTO>('/api/join/qr', { method: 'POST', body }),
    onSuccess: (session) => {
      forgetViewerData(qc, 'student')
      adoptSession(qc, session)
    },
  })
}

export function useJoinChoices() {
  return useQuery<JoinChoicesDTO, ApiError>({
    queryKey: keys.join.choices(),
    queryFn: () => api<JoinChoicesDTO>('/api/join/choices'),
  })
}

export function useJoinChoose() {
  const qc = useQueryClient()
  return useMutation<SessionDTO, ApiError, { galleyId: string }>({
    mutationFn: (body) => api<SessionDTO>('/api/join/choose', { method: 'POST', body }),
    onSuccess: (session) => {
      forgetViewerData(qc, 'student')
      adoptSession(qc, session)
    },
  })
}

// ---------------------------------------------------------------------------
// §4.2 Teacher: galleys, courses, rooms, pieces, members
// ---------------------------------------------------------------------------

export function useTeacherGalleys() {
  return useQuery<GalleySummaryDTO[], ApiError>({
    queryKey: keys.teacher.galleys(),
    queryFn: () => api<GalleySummaryDTO[]>('/api/t/galleys'),
  })
}

/** Galleys this teacher deleted in the last 30 days (restorable with PATCH {restore:true}). */
export function useDeletedGalleys() {
  return useQuery<GalleySummaryDTO[], ApiError>({
    queryKey: keys.teacher.deletedGalleys(),
    queryFn: () => api<GalleySummaryDTO[]>('/api/t/galleys', { query: { deleted: '1' } }),
  })
}

export function useCourses() {
  return useQuery<CourseDTO[], ApiError>({
    queryKey: keys.teacher.courses(),
    queryFn: () => api<CourseDTO[]>('/api/t/courses'),
    // Each fetch calls Google Classroom; courses rarely change within a session.
    staleTime: 5 * 60_000,
  })
}

export function useCreateGalley() {
  const qc = useQueryClient()
  return useMutation<
    GalleyDetailDTO,
    ApiError,
    { courseId: string; gradeBand?: GradeBand; theme?: ThemeId }
  >({
    mutationFn: (body) => api<GalleyDetailDTO>('/api/t/galleys', { method: 'POST', body }),
    onSuccess: (galley) => {
      qc.setQueryData(keys.teacher.galley(galley.id), galley)
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
      void qc.invalidateQueries({ queryKey: keys.teacher.courses() })
    },
  })
}

export function useGalley(galleyId: string | undefined) {
  return useQuery<GalleyDetailDTO, ApiError>({
    queryKey: keys.teacher.galley(galleyId ?? ''),
    queryFn: galleyId ? () => api<GalleyDetailDTO>(`/api/t/galleys/${enc(galleyId)}`) : skipToken,
  })
}

export function useUpdateGalley(galleyId: string) {
  const qc = useQueryClient()
  return useMutation<GalleyDetailDTO, ApiError, GalleyPatch>({
    mutationFn: (body) =>
      api<GalleyDetailDTO>(`/api/t/galleys/${enc(galleyId)}`, { method: 'PATCH', body }),
    onSuccess: (galley) => {
      qc.setQueryData(keys.teacher.galley(galley.id), galley)
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
      void qc.invalidateQueries({ queryKey: keys.world.galley(galley.id) })
    },
  })
}

/** Soft delete (30 days). Undo with useUpdateGalley(id).mutate({ restore: true }). */
export function useDeleteGalley() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { galleyId: string }>({
    mutationFn: ({ galleyId }) =>
      api<void>(`/api/t/galleys/${enc(galleyId)}`, { method: 'DELETE' }),
    onSuccess: (_data, { galleyId }) => {
      qc.removeQueries({ queryKey: keys.world.galley(galleyId) })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
      void qc.invalidateQueries({ queryKey: keys.teacher.galley(galleyId) })
    },
  })
}

export function useSyncGalley(galleyId: string) {
  const qc = useQueryClient()
  return useMutation<GalleyDetailDTO, ApiError, void>({
    mutationFn: () =>
      api<GalleyDetailDTO>(`/api/t/galleys/${enc(galleyId)}/sync`, { method: 'POST' }),
    onSuccess: (galley) => {
      qc.setQueryData(keys.teacher.galley(galley.id), galley)
      void qc.invalidateQueries({ queryKey: keys.teacher.coursework(galley.id) })
      void qc.invalidateQueries({ queryKey: keys.world.galley(galley.id) })
    },
  })
}

export function useCoursework(galleyId: string | undefined) {
  return useQuery<CourseworkDTO[], ApiError>({
    queryKey: keys.teacher.coursework(galleyId ?? ''),
    queryFn: galleyId
      ? () => api<CourseworkDTO[]>(`/api/t/galleys/${enc(galleyId)}/coursework`)
      : skipToken,
    staleTime: 2 * 60_000,
  })
}

export function useCreateRoom(galleyId: string) {
  const qc = useQueryClient()
  return useMutation<
    RoomDetailDTO,
    ApiError,
    { courseworkId: string; displayOverride?: DisplayType }
  >({
    mutationFn: (body) =>
      api<RoomDetailDTO>(`/api/t/galleys/${enc(galleyId)}/rooms`, { method: 'POST', body }),
    onSuccess: (room) => {
      qc.setQueryData(keys.teacher.room(room.id), room)
      void qc.invalidateQueries({ queryKey: keys.teacher.galley(galleyId) })
      void qc.invalidateQueries({ queryKey: keys.teacher.coursework(galleyId) })
      void qc.invalidateQueries({ queryKey: keys.world.galley(galleyId) })
    },
  })
}

export function useRoom(roomId: string | undefined) {
  return useQuery<RoomDetailDTO, ApiError>({
    queryKey: keys.teacher.room(roomId ?? ''),
    queryFn: roomId ? () => api<RoomDetailDTO>(`/api/t/rooms/${enc(roomId)}`) : skipToken,
  })
}

/** Room responses don't name their galley, so refresh every cached galley and world. */
function afterRoomChange(qc: QueryClient, room: RoomDetailDTO): void {
  qc.setQueryData(keys.teacher.room(room.id), room)
  void qc.invalidateQueries({ queryKey: keys.teacher.galleyAll() })
  void qc.invalidateQueries({ queryKey: keys.world.all() })
}

export function useUpdateRoom(roomId: string) {
  const qc = useQueryClient()
  return useMutation<RoomDetailDTO, ApiError, RoomPatch>({
    mutationFn: (body) =>
      api<RoomDetailDTO>(`/api/t/rooms/${enc(roomId)}`, { method: 'PATCH', body }),
    onSuccess: (room) => afterRoomChange(qc, room),
  })
}

/** Import the Drive files the teacher granted in the Picker. */
export function useRoomPicked(roomId: string) {
  const qc = useQueryClient()
  return useMutation<RoomDetailDTO, ApiError, { fileIds: string[] }>({
    mutationFn: (body) =>
      api<RoomDetailDTO>(`/api/t/rooms/${enc(roomId)}/picked`, { method: 'POST', body }),
    onSuccess: (room) => afterRoomChange(qc, room),
  })
}

export function useSyncRoom(roomId: string) {
  const qc = useQueryClient()
  return useMutation<RoomDetailDTO, ApiError, void>({
    mutationFn: () => api<RoomDetailDTO>(`/api/t/rooms/${enc(roomId)}/sync`, { method: 'POST' }),
    onSuccess: (room) => afterRoomChange(qc, room),
  })
}

/** "Tidy up": the only full reflow of a room's layout. */
export function useTidyRoom(roomId: string) {
  const qc = useQueryClient()
  return useMutation<RoomDetailDTO, ApiError, void>({
    mutationFn: () => api<RoomDetailDTO>(`/api/t/rooms/${enc(roomId)}/tidy`, { method: 'POST' }),
    onSuccess: (room) => afterRoomChange(qc, room),
  })
}

export function useUpdatePiece() {
  const qc = useQueryClient()
  return useMutation<PieceTeacherDTO, ApiError, { pieceId: string } & PiecePatch>({
    mutationFn: ({ pieceId, ...body }) =>
      api<PieceTeacherDTO>(`/api/t/pieces/${enc(pieceId)}`, { method: 'PATCH', body }),
    onSuccess: (piece) => {
      qc.setQueryData<RoomDetailDTO>(keys.teacher.room(piece.roomId), (room) =>
        room ? { ...room, pieces: room.pieces.map((p) => (p.id === piece.id ? piece : p)) } : room,
      )
      void qc.invalidateQueries({ queryKey: keys.teacher.room(piece.roomId) })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

export function useUpdateMember() {
  const qc = useQueryClient()
  return useMutation<MemberDTO, ApiError, { memberId: string } & MemberPatch>({
    mutationFn: ({ memberId, ...body }) =>
      api<MemberDTO>(`/api/t/members/${enc(memberId)}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.teacher.galleyAll() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/** Issue a new card for one student (the old card stops working). */
export function useResetCard() {
  const qc = useQueryClient()
  return useMutation<SignInCardDTO, ApiError, { memberId: string }>({
    mutationFn: ({ memberId }) =>
      api<SignInCardDTO>(`/api/t/members/${enc(memberId)}/card-reset`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.teacher.cardsAll() }),
  })
}

export function useSignInCards(galleyId: string | undefined) {
  return useQuery<SignInCardsDTO, ApiError>({
    queryKey: keys.teacher.cards(galleyId ?? ''),
    queryFn: galleyId
      ? () => api<SignInCardsDTO>(`/api/t/galleys/${enc(galleyId)}/cards`)
      : skipToken,
  })
}

// ---------------------------------------------------------------------------
// §4.2 Teacher: walks, review, debrief
// ---------------------------------------------------------------------------

export function useStartWalk(galleyId: string) {
  const qc = useQueryClient()
  return useMutation<WalkConsoleDTO, ApiError, WalkSettingsInput>({
    mutationFn: (body) =>
      api<WalkConsoleDTO>(`/api/t/galleys/${enc(galleyId)}/walks`, { method: 'POST', body }),
    onSuccess: (walk) => {
      qc.setQueryData(keys.teacher.walk(walk.walk.id), walk)
      void qc.invalidateQueries({ queryKey: keys.teacher.galley(galleyId) })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
      void qc.invalidateQueries({ queryKey: keys.world.galley(galleyId) })
    },
  })
}

export function useWalkConsole(walkId: string | undefined) {
  return useQuery<WalkConsoleDTO, ApiError>({
    queryKey: keys.teacher.walk(walkId ?? ''),
    queryFn: walkId ? () => api<WalkConsoleDTO>(`/api/t/walks/${enc(walkId)}`) : skipToken,
  })
}

/** Pause, lock notes, end, reopen, delete (soft) and their undos. */
export function useWalkControl(walkId: string) {
  const qc = useQueryClient()
  return useMutation<WalkConsoleDTO, ApiError, { action: WalkControlAction }>({
    mutationFn: (body) =>
      api<WalkConsoleDTO>(`/api/t/walks/${enc(walkId)}/control`, { method: 'POST', body }),
    onSuccess: (walk) => {
      qc.setQueryData(keys.teacher.walk(walkId), walk)
      void qc.invalidateQueries({ queryKey: keys.teacher.review(walkId) })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleyAll() })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

export function useReviewQueue(walkId: string | undefined) {
  return useQuery<ReviewQueueDTO, ApiError>({
    queryKey: keys.teacher.review(walkId ?? ''),
    queryFn: walkId ? () => api<ReviewQueueDTO>(`/api/t/walks/${enc(walkId)}/review`) : skipToken,
  })
}

export function useModerateNotes() {
  const qc = useQueryClient()
  return useMutation<
    { notes: NoteDTO[] },
    ApiError,
    { noteIds: string[]; action: ModerationAction; nudge?: string }
  >({
    mutationFn: (body) =>
      api<{ notes: NoteDTO[] }>('/api/t/notes/moderate', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.teacher.reviewAll() })
      void qc.invalidateQueries({ queryKey: keys.teacher.walkAll() })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleyAll() })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
    },
  })
}

/** Return: every approved note reaches its creators at once. */
export function useReturnWalk(walkId: string) {
  const qc = useQueryClient()
  return useMutation<WalkConsoleDTO, ApiError, void>({
    mutationFn: () => api<WalkConsoleDTO>(`/api/t/walks/${enc(walkId)}/return`, { method: 'POST' }),
    onSuccess: (walk) => {
      qc.setQueryData(keys.teacher.walk(walkId), walk)
      void qc.invalidateQueries({ queryKey: keys.teacher.review(walkId) })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleyAll() })
      void qc.invalidateQueries({ queryKey: keys.teacher.galleys() })
    },
  })
}

export function useDebrief(walkId: string | undefined) {
  return useQuery<DebriefDTO, ApiError>({
    queryKey: keys.teacher.debrief(walkId ?? ''),
    queryFn: walkId ? () => api<DebriefDTO>(`/api/t/walks/${enc(walkId)}/debrief`) : skipToken,
  })
}

export function useUpdateDebrief(walkId: string) {
  const qc = useQueryClient()
  return useMutation<DebriefDTO, ApiError, { picks: string[] }>({
    mutationFn: (body) =>
      api<DebriefDTO>(`/api/t/walks/${enc(walkId)}/debrief`, { method: 'PATCH', body }),
    onSuccess: (debrief) => qc.setQueryData(keys.teacher.debrief(walkId), debrief),
  })
}

// ---------------------------------------------------------------------------
// §4.2 Teacher: family links, export, links, AI
// ---------------------------------------------------------------------------

export function useFamilyLinks(galleyId: string | undefined) {
  return useQuery<FamilyLinkDTO[], ApiError>({
    queryKey: keys.teacher.family(galleyId ?? ''),
    queryFn: galleyId
      ? () => api<FamilyLinkDTO[]>(`/api/t/galleys/${enc(galleyId)}/family`)
      : skipToken,
  })
}

export function useCreateFamilyLinks(galleyId: string) {
  const qc = useQueryClient()
  return useMutation<FamilyLinkDTO[], ApiError, { memberIds: string[] | 'all' }>({
    mutationFn: (body) =>
      api<FamilyLinkDTO[]>(`/api/t/galleys/${enc(galleyId)}/family`, { method: 'POST', body }),
    onSuccess: (links) => qc.setQueryData(keys.teacher.family(galleyId), links),
  })
}

export function useRevokeFamilyLink() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { linkId: string }>({
    mutationFn: ({ linkId }) => api<void>(`/api/t/family/${enc(linkId)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.teacher.familyAll() }),
  })
}

/** JSON export of a galley; use as an <a href download> target (cookies go with it). */
export function galleyExportUrl(galleyId: string): string {
  return `/api/t/galleys/${enc(galleyId)}/export`
}

/** Paste-a-link: map a Google Classroom URL to the matching Galley page. */
export function useResolveLink() {
  return useMutation<ResolveLinkDTO, ApiError, { url: string }>({
    mutationFn: (body) => api<ResolveLinkDTO>('/api/t/resolve-link', { method: 'POST', body }),
  })
}

/** Optional AI helper (403 when AI is off): draft a room sign from the assignment text. */
export function useAiRoomSign() {
  return useMutation<{ text: string; label: 'AI draft' }, ApiError, { roomId: string }>({
    mutationFn: (body) =>
      api<{ text: string; label: 'AI draft' }>('/api/t/ai/room-sign', { method: 'POST', body }),
  })
}

/** Optional AI helper (403 when AI is off): group common wonders for the debrief. */
export function useAiWonders() {
  return useMutation<
    { groups: Array<{ theme: string; noteIds: string[] }> },
    ApiError,
    { walkId: string }
  >({
    mutationFn: (body) =>
      api<{ groups: Array<{ theme: string; noteIds: string[] }> }>('/api/t/ai/wonders', {
        method: 'POST',
        body,
      }),
  })
}

// ---------------------------------------------------------------------------
// §4.3 World (teacher, student member, or family token)
// ---------------------------------------------------------------------------

/**
 * Which perspective to load the world in. The server picks the teacher session by default;
 * `as: 'student'` selects the student session (a demo browser may hold both), `as: 'projector'`
 * asks for the class-visible teacher view, and `f` is a family-link token.
 */
export interface WorldView {
  as?: 'student' | 'projector' | null
  f?: string | null
}

/** Stable cache-key fragment for a WorldView. */
export function worldViewKey(view?: WorldView | null): string | null {
  if (view?.f) return `f:${view.f}`
  return view?.as ?? null
}

function worldQuery(view?: WorldView | null) {
  return { as: view?.f ? null : (view?.as ?? null), f: view?.f ?? null }
}

export function useWorld(galleyId: string | undefined, view?: WorldView | null) {
  return useQuery<WorldDTO, ApiError>({
    queryKey: keys.world.world(galleyId ?? '', worldViewKey(view)),
    queryFn: galleyId
      ? () => api<WorldDTO>(`/api/g/${enc(galleyId)}/world`, { query: worldQuery(view) })
      : skipToken,
  })
}

export function usePiece(
  galleyId: string | undefined,
  pieceId: string | undefined,
  view?: WorldView | null,
) {
  return useQuery<PieceDetailDTO, ApiError>({
    queryKey: keys.world.piece(galleyId ?? '', pieceId ?? '', worldViewKey(view)),
    queryFn:
      galleyId && pieceId
        ? () =>
            api<PieceDetailDTO>(`/api/g/${enc(galleyId)}/pieces/${enc(pieceId)}`, {
              query: worldQuery(view),
            })
        : skipToken,
  })
}

/**
 * Record a visit so "new" badges reset next time. Deliberately no invalidation: badges
 * should stay put for the rest of this visit.
 */
export function useMarkVisited(galleyId: string, view?: WorldView | null) {
  return useMutation<void, ApiError, void>({
    // ?as=student makes a demo browser that holds both sessions record the student's visit.
    mutationFn: () =>
      api<void>(`/api/g/${enc(galleyId)}/visited`, { method: 'POST', query: worldQuery(view) }),
  })
}

// ---------------------------------------------------------------------------
// §4.4 Student
// ---------------------------------------------------------------------------

export function useStudentMe(enabled = true) {
  return useQuery<StudentMeDTO, ApiError>({
    queryKey: keys.student.me(),
    queryFn: enabled ? () => api<StudentMeDTO>('/api/s/me') : skipToken,
  })
}

/** Opening a stop's Reader unlocks its note box. */
export function useOpenStop() {
  const qc = useQueryClient()
  return useMutation<StopDTO, ApiError, { stopId: string }>({
    mutationFn: ({ stopId }) =>
      api<StopDTO>(`/api/s/stops/${enc(stopId)}/open`, { method: 'POST' }),
    onSuccess: (stop) => {
      patchStop(qc, stop)
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/**
 * Send a note. A 400 carries the NoteValidation in the error details; a generic note comes
 * back with a nudge until the student confirms with `confirmGeneric: true`.
 */
export function useSendNote() {
  const qc = useQueryClient()
  return useMutation<SendNoteResult, ApiError, NoteInput & { confirmGeneric?: boolean }>({
    mutationFn: (body) => api<SendNoteResult>('/api/s/notes', { method: 'POST', body }),
    onSuccess: ({ stop }) => {
      patchStop(qc, stop)
      void qc.invalidateQueries({ queryKey: keys.student.me() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/** Unsend within NOTE_UNSEND_SECONDS. */
export function useUnsendNote() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { noteId: string }>({
    mutationFn: ({ noteId }) => api<void>(`/api/s/notes/${enc(noteId)}/unsend`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.student.me() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/** "One more stop". 404 with code "none_left" when every piece has enough visitors. */
export function useOneMoreStop(walkId: string) {
  const qc = useQueryClient()
  return useMutation<StopDTO, ApiError, void>({
    mutationFn: () => api<StopDTO>(`/api/s/walks/${enc(walkId)}/one-more`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.student.me() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/** Feedback practice before a class's first walk. The answer format belongs to the practice screen. */
export function usePractice() {
  const qc = useQueryClient()
  return useMutation<StudentDTO, ApiError, { answers: unknown }>({
    mutationFn: (body) => api<StudentDTO>('/api/s/practice', { method: 'POST', body }),
    onSuccess: (student) => {
      qc.setQueryData<SessionDTO>(keys.session(), (s) => (s ? { ...s, student } : s))
      void qc.invalidateQueries({ queryKey: keys.student.me() })
    },
  })
}

export function useWalkReturn(walkId: string | undefined) {
  return useQuery<ReturnDTO, ApiError>({
    queryKey: keys.student.walkReturn(walkId ?? ''),
    queryFn: walkId ? () => api<ReturnDTO>(`/api/s/walks/${enc(walkId)}/return`) : skipToken,
  })
}

export function useReflect(walkId: string) {
  const qc = useQueryClient()
  return useMutation<ReturnDTO, ApiError, ReflectionInput>({
    mutationFn: (body) =>
      api<ReturnDTO>(`/api/s/walks/${enc(walkId)}/reflect`, { method: 'POST', body }),
    onSuccess: (ret) => {
      qc.setQueryData(keys.student.walkReturn(walkId), ret)
      void qc.invalidateQueries({ queryKey: keys.student.me() })
    },
  })
}

/** Students change only their own piece; a teacher override can only make it more private. */
export function useUpdateMyPiece() {
  const qc = useQueryClient()
  return useMutation<PieceSummaryDTO, ApiError, { pieceId: string } & MyPiecePatch>({
    mutationFn: ({ pieceId, ...body }) =>
      api<PieceSummaryDTO>(`/api/s/pieces/${enc(pieceId)}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.student.me() })
      void qc.invalidateQueries({ queryKey: keys.world.all() })
    },
  })
}

/** Persist a student's Comfort settings so they follow them to another Chromebook. */
export function useUpdateComfort() {
  const qc = useQueryClient()
  return useMutation<StudentDTO, ApiError, Partial<ComfortSettings>>({
    mutationFn: (body) => api<StudentDTO>('/api/s/comfort', { method: 'PATCH', body }),
    onSuccess: (student) => {
      qc.setQueryData<SessionDTO>(keys.session(), (s) => (s ? { ...s, student } : s))
      qc.setQueryData<StudentMeDTO>(keys.student.me(), (me) => (me ? { ...me, student } : me))
    },
  })
}

// ---------------------------------------------------------------------------
// §4.5 Family, instance
// ---------------------------------------------------------------------------

export function useFamilyView(token: string | undefined) {
  return useQuery<FamilyViewDTO, ApiError>({
    queryKey: keys.family.view(token ?? ''),
    queryFn: token ? () => api<FamilyViewDTO>(`/api/f/${enc(token)}`) : skipToken,
  })
}

export function useTechKit() {
  return useQuery<TechKitDTO, ApiError>({
    queryKey: keys.instance.kit(),
    queryFn: () => api<TechKitDTO>('/api/instance/kit'),
    staleTime: 10 * 60_000,
  })
}
